import type { SetCurrentYearIdActionPayload } from './years.slice.types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { YearsState } from 'shared/interfaces/years.interface';

import { createSlice } from '@reduxjs/toolkit';
import {
  getCurrentYearFromLS,
  getCurrentYearFrozenFromLS,
  getCurrentYearIdFromLS,
} from 'shared/utils';

import {
  createYearThunk,
  getYearsThunk,
  removeYearThunk,
  updateYearThunk,
} from './years.thunks';

const initialState: YearsState = {
  list: [],
  isGetYearsThunkPending: false,
  getYearsThunkError: null,
  isCreateYearThunkPending: false,
  createYearThunkError: null,
  isUpdateYearThunkPending: false,
  updateYearThunkError: null,
  isRemoveYearThunkPending: false,
  removeYearThunkError: null,
  currentYearId: getCurrentYearIdFromLS(),
  currentYearName: getCurrentYearFromLS(),
  currentYearFrozen: getCurrentYearFrozenFromLS(),
};

const yearsSlice = createSlice({
  name: 'years',
  initialState,
  reducers: {
    setCurrentYearIdAction: (
      state,
      action: PayloadAction<SetCurrentYearIdActionPayload>,
    ) => {
      const { id, name, isFrozen, isYearGoalFrozen } = action.payload;

      localStorage.setItem('currentYearId', String(id));
      localStorage.setItem('currentYear', String(name));
      localStorage.setItem('currentYearFrozen', String(isFrozen));
      localStorage.setItem('currentYearGoalFrozen', String(isYearGoalFrozen));

      state.currentYearId = id;
      state.currentYearName = name;
      state.currentYearFrozen = isFrozen;
    },
    clearCurrentYearDataAction: (state) => {
      state.currentYearId = null;
      state.currentYearName = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getYearsThunk.pending, (state) => {
        state.getYearsThunkError = null;
        state.isGetYearsThunkPending = true;
      })
      .addCase(getYearsThunk.fulfilled, (state, action) => {
        state.list = action.payload.rows;
        state.isGetYearsThunkPending = false;
      })
      .addCase(getYearsThunk.rejected, (state, action) => {
        state.getYearsThunkError =
          action.error.message || 'Failed to fetch years';
        state.isGetYearsThunkPending = false;
      });

    builder
      .addCase(createYearThunk.pending, (state) => {
        state.createYearThunkError = null;
        state.isCreateYearThunkPending = true;
      })
      .addCase(createYearThunk.fulfilled, (state, action) => {
        const { newYear } = action.payload;

        state.list.push(newYear);
        state.isCreateYearThunkPending = false;
      })
      .addCase(createYearThunk.rejected, (state, action) => {
        state.createYearThunkError =
          action.error.message || 'Failed to create year';
        state.isCreateYearThunkPending = false;
      });

    builder
      .addCase(updateYearThunk.pending, (state) => {
        state.updateYearThunkError = null;
        state.isUpdateYearThunkPending = true;
      })
      .addCase(updateYearThunk.fulfilled, (state, action) => {
        const updatedYear = action.payload;

        state.list = state.list.map((year) =>
          year.id === updatedYear.id ? updatedYear : year,
        );
        state.isUpdateYearThunkPending = false;
      })
      .addCase(updateYearThunk.rejected, (state, action) => {
        state.updateYearThunkError =
          action.error.message || 'Failed to update year';
        state.isUpdateYearThunkPending = false;
      });

    builder
      .addCase(removeYearThunk.pending, (state) => {
        state.removeYearThunkError = null;
        state.isRemoveYearThunkPending = true;
      })
      .addCase(removeYearThunk.fulfilled, (state, action) => {
        const id = action.payload;
        const isCurrent = state.currentYearId === id;

        state.list = state.list.filter((year) => year.id !== id);

        if (isCurrent) {
          state.currentYearId = null;
          state.currentYearName = null;
        }

        state.isRemoveYearThunkPending = false;
      })
      .addCase(removeYearThunk.rejected, (state, action) => {
        state.removeYearThunkError =
          action.error.message || 'Failed to remove year';
        state.isRemoveYearThunkPending = false;
      });
  },
});

export const { clearCurrentYearDataAction, setCurrentYearIdAction } =
  yearsSlice.actions;

export const yearsReducer = yearsSlice.reducer;
